import {
  Alert,
  AlertProps,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { Box } from '@mui/system';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../../shared/redux/store';
import { fetchRequestFromSearch, reviewRequest } from '../../requestSearchSlice';
import { ReactNode, useEffect, useState } from 'react';
import { ArrowForwardIosSharp } from '@mui/icons-material';
import { useLocation, useSearchParams } from 'react-router-dom';
import DocumentTypes from '../../../../models/DocumentTypes';
import { EquivalencyRequestSearchParams } from '../../../../models/EquivalencyRequest';
import { useRecaptcha } from '../../../../shared/google-recaptcha-v3/useRecaptcha';
import googleRecaptchaActions from '../../../../shared/google-recaptcha-v3/googleRecaptchaActions';
import { popSnackbar } from '../../../SnackbarProvider/snackbarSlice';

const RequestReview = ({ canSearch, researchRequest }: { canSearch: boolean, researchRequest: () => void }) => {
  const request = useSelector((state: RootState) => state.requestSearch.searchRequest);
  const requestReview = request?.equivalencies?.review ? [request?.equivalencies?.review] : [];
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [searchUrlParams] = useSearchParams(location.search);
  const { callIfHuman, reCaptchaToken } = useRecaptcha(googleRecaptchaActions.requestSearch);
  const [_searched, setSearched] = useState(false);
  const [caseNumber] = useState('');
  const [id, setId] = useState(request.equivalencies?.reviewRequest);
  const [documentType] = useState(DocumentTypes.dni);
  const [documentNumber] = useState('');
  
  const searchParams: EquivalencyRequestSearchParams = {
    documentType,
    documentNumber,
    caseNumber,
    id,
    reCaptchaToken,
    offset: 0,
    pageSize: 1
  };
  const clickReviewed = (searchParams: EquivalencyRequestSearchParams) => callIfHuman(() => {
    setSearched(true);
    dispatch(fetchRequestFromSearch(searchParams));
  });

  const [caseReview, setCaseReview] = useState('');
  const [expandedAll, setExpandedAll] = useState(true);

  const handleChangeExpandedComment = () => {
    setExpandedAll(!expandedAll);
  };

  const classNameArrowIcon = () => {
    return expandedAll ? 'expanded-all' : 'collapse-all';
  };

  const handleCaseReview = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCaseReview(value);
  };

  const sendReview = () => {
    const data = {
      requestId: request.id,
      review: caseReview
    }
    dispatch(reviewRequest(data))
    .then(() => {
      researchRequest();
      dispatch(popSnackbar({
        type: 'success',
        message: 'Se envió el comentario correctamente.',
        duration: 5000
      }));
    })
    .catch(() => {
      dispatch(popSnackbar({
        type: 'success',
        message: 'Hubo un problema al enviar el comentario.',
        duration: 5000
      }));
    });
  }

  useEffect(() => {
    if (request)
    setId(request?.equivalencies?.reviewRequest);
  }, [request]);

  return (
    <Paper variant="outlined" sx={{ width: '100%', marginTop: '40px' }}>
      <TableContainer>
        <Table className="EQXXI-RequestDetail-ActivitiesTable" size="small">
          <TableHead>
            <TableRow>
              <TableCell
                className="EQXXI-RequestDetail-Name"
                sx={{
                  marginLeft: 0,
                  backgroundColor: '#00a98f',
                  padding: '16px'
                }}
                align="left"
              >
                <Box sx={{ display: 'flex' }}>
                  <Button
                    className={`EQXXI-RequestDetail-Switch ${classNameArrowIcon()}`}
                    onClick={handleChangeExpandedComment}
                  >
                    <ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />
                  </Button>
                  <Typography>
                    {t('requestReviewTtile') as unknown as ReactNode}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            className="EQXXI-Equivalency-TableBody"
            style={{ overflow: 'auto', maxHeight: '390px' }}
          >
            {expandedAll ? (
              <Box>
                {requestReview.length > 0 ? (
                  <>
                    {request?.equivalencies?.reviewed ? (
                      <Alert
                        color={'primary' as AlertProps['color']}
                        severity="info"
                        style={{ margin: '16px', background: '#FFF' }}
                      >
                        {t('requestReviewed') as unknown as ReactNode}
                      </Alert>
                    ) : (
                      <Alert
                        color={'primary' as AlertProps['color']}
                        severity="info"
                        style={{ margin: '16px', background: '#FFF' }}
                      >
                        {t('requestReviewInvalid') as unknown as ReactNode}
                      </Alert>
                    )}
                    <TableContainer sx={{ padding: '0 16px 16px' }}>
                      <Table
                        className="EQXXI-RequestRequest-ActivitiesTable"
                        size="small"
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell
                              className="EQXXI-RequestRequest-Name"
                              sx={{
                                marginLeft: 0,
                                backgroundColor: '#00a98f',
                                padding: '16px'
                              }}
                            >
                              <Typography>Revisión</Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody
                          data-testid="review-request-table-body"
                          className="EQXXI-RequestRequest-TableBody"
                          style={{ overflow: 'auto', maxHeight: '390px' }}
                        >
                          {requestReview.map(
                            (row: { description: string }, i: number) => (
                              <TableRow key={i} component={'tr'}>
                                <TableCell
                                  className="EQXXI-RequestSimulateRow-Name"
                                  style={{ padding: '16px' }}
                                >
                                  <Typography data-testid="review-request-name">
                                    {row.description}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ padding: '0 16px 16px' }}>
                      <Button onClick={() => clickReviewed(searchParams)}>{t('newReviewRequest') as unknown as ReactNode}</Button>
                    </Box>
                  </>
                ) : (
                  <Box
                    className="REQXXI-Search-Fields"
                    style={{ margin: '16px' }}
                  >
                    <Alert
                      color={'primary' as AlertProps['color']}
                      severity="info"
                      style={{ background: '#FFF' }}
                    >
                      {t('requestReview') as unknown as ReactNode}
                    </Alert>
                    <TextField
                      multiline
                      rows={2}
                      maxRows={4}
                      fullWidth
                      tabIndex={-1}
                      data-testid={'comment-input'}
                      label={<>{t('reviewLabel')}</>}
                      value={caseReview || ''}
                      onChange={handleCaseReview}
                    />
                    <Box>
                      <Button
                        id="searchEquivalencyRequest"
                        disabled={!canSearch}
                        className="REQXXI-Search-Button"
                        variant="contained"
                        onClick={() => sendReview()}
                        data-testid="send-button"
                      >
                        Enviar
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default RequestReview;
